import { selectAllowUpgrade, selectIntpWebsite, selectStpLimit } from '@va/aaas/shared/selectors';
import { is3ASUnlimitedFreeTrialPlan } from '@va/constants';
import { startUpgradeProcess } from '@va/dashboard/actions/app';
import { getIntpResetDate } from '@va/dashboard/selectors/app';
import { useLocalizationContext } from '@va/localization';
import { hasPermission } from '@va/standalone/shared/selectors';
import { paragraphSizes, ParagraphWithTooltip } from '@va/ui/design-system';
import { getLocalizedDate, is3asApp, isWixMiniApp } from '@va/util/helpers';
import { useIsHovering } from '@va/util/hooks';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export function NextResetDate() {
  const dispatch = useDispatch();
  const { translate, locale } = useLocalizationContext();
  const nextResetDateRef = useRef<HTMLParagraphElement>(null);
  const isHovering = useIsHovering(nextResetDateRef);
  const intpResetDate = useSelector(getIntpResetDate);
  const hasUpgradePermission = useSelector(hasPermission);
  const nextResetDate = is3asApp() || isWixMiniApp() ? dayjs(intpResetDate) : dayjs().add(1, 'month').startOf('month');
  const formattedNextResetDate = getLocalizedDate(nextResetDate.unix(), locale);

  return (
    <div ref={nextResetDateRef} className='min-w-180px'>
      <ParagraphWithTooltip
        size={paragraphSizes.tiny}
        className='cursor-pointer font-525 xxs:w-160px'
        colorClassName={classNames({
          'text-primary-bright-50 underline': isHovering,
          'text-white-75': !isHovering,
        })}
        onClick={() => {
          if (!hasUpgradePermission) return;
          dispatch(startUpgradeProcess());
        }}
      >
        {isHovering && hasUpgradePermission
          ? translate('featureCounter.increaseLimits')
          : translate('featureCounter.nextResetDate', { nextResetDate: formattedNextResetDate })}
      </ParagraphWithTooltip>
    </div>
  );
}

export function NextResetDate3AS() {
  const dispatch = useDispatch();
  const { translate, locale } = useLocalizationContext();
  const nextResetDateRef = useRef<HTMLParagraphElement>(null);
  const isHovering = useIsHovering(nextResetDateRef);
  const intpResetDate = useSelector(getIntpResetDate);
  const intpWebsite = useSelector(selectIntpWebsite);
  const allowUpgrade = useSelector(selectAllowUpgrade);
  const stpLimit = useSelector(selectStpLimit);

  const isUnlimitedFreeTrial = useMemo(() => {
    return stpLimit === -1 && is3ASUnlimitedFreeTrialPlan(intpWebsite.packageName);
  }, [intpWebsite.packageName, stpLimit]);

  const aaasTrialEndsOn = dayjs(intpWebsite.expiresAt);
  const aaasTrialEndingDate = getLocalizedDate(aaasTrialEndsOn.unix(), locale);

  const nextResetDate = dayjs(intpResetDate);
  const formattedNextResetDate = getLocalizedDate(nextResetDate.unix(), locale);

  const handleClick = () => {
    if (!allowUpgrade) return;
    dispatch(startUpgradeProcess());
  };

  const label = useMemo(() => {
    if (allowUpgrade && isHovering) {
      return translate('featureCounter.increaseLimits');
    }

    if (isUnlimitedFreeTrial) {
      return translate('featureCounter.freeTrialEndsOn', { endDate: aaasTrialEndingDate });
    }

    return translate('featureCounter.nextResetDate', { nextResetDate: formattedNextResetDate });
  }, [isHovering, allowUpgrade, isUnlimitedFreeTrial, translate, formattedNextResetDate, aaasTrialEndingDate]);

  return (
    <div ref={nextResetDateRef} className='min-w-180px'>
      <ParagraphWithTooltip
        size={paragraphSizes.tiny}
        className='font-525 xxs:w-160px'
        colorClassName={classNames({
          'text-primary-bright-50 underline': isHovering && allowUpgrade,
          'text-white-75': !isHovering && allowUpgrade,
          'cursor-pointer': allowUpgrade,
        })}
        onClick={handleClick}
      >
        {label}
      </ParagraphWithTooltip>
    </div>
  );
}
